import { useEffect, useRef, useState } from 'react';
import { websocketService, StreamSubscription } from '../lib/websocket-service';
import { MONAMI_HOST } from '../config/env';
import { createLogger, LogLevel } from '../lib/logger';
import { fetchWithTimeout } from '../lib/fetch-utils';
import { getDeviceToken } from '@/db/services/device_service';
import { markDeletedOnline } from '@/db/services/document_service';
import db from '@/db';

const logger = createLogger('useDocumentWebSocket', LogLevel.NONE);

export interface DocumentUpdate {
  content: string;
  timestamp: string;
}

export interface UseDocumentWebSocketOptions {
  clientId: number | null;
  documentId: number | null;
  personId: number | null;
  enabled?: boolean;
  onUpdate?: (update: DocumentUpdate, streamKey: string) => void;
  onError?: (error: Error) => void;
}

export function useDocumentWebSocket(options: UseDocumentWebSocketOptions) {
  const { clientId, documentId, personId, enabled = true, onUpdate, onError } = options;

  const subscriptionsRef = useRef<StreamSubscription[]>([]);
  const mountedRef = useRef(true);
  const onUpdateRef = useRef(onUpdate);
  const onErrorRef = useRef(onError);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Keep callback refs up to date
  useEffect(() => {
    onUpdateRef.current = onUpdate;
    onErrorRef.current = onError;
  });

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      logger.info('Network is now online');
      setIsOnline(true);
    };

    const handleOffline = () => {
      logger.info('Network is now offline');
      setIsOnline(false);
      setConnected(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Initialize WebSocket service once (only when online)
  useEffect(() => {
    if (isOnline) {
      try {
        websocketService.initialize({ url: MONAMI_HOST });
      } catch (error) {
        logger.error('Failed to initialize WebSocket service', error);
      }
    }
  }, [isOnline]);

  // Track component mount status
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Subscribe to document updates
  useEffect(() => {
    if (!enabled || !clientId || !documentId || !personId || !isOnline) {
      if (!isOnline && enabled && clientId && documentId && personId) {
        logger.debug('Skipping subscription - offline', { documentId });
      }
      return;
    }

    let cancelled = false;

    const subscribe = async () => {
      try {
        // Fetch the signed stream names from the backend. Offline API auth is the
        // device token (Bearer), not the session cookie — mirror fetchWithAuth's
        // token logic here so the request authenticates (a plain cookie fetch is
        // rejected by authenticate_device! with a 401).
        const deviceToken = await getDeviceToken();
        const response = await fetchWithTimeout(
          `${MONAMI_HOST}/api/offline/clients/${clientId}/documents/${documentId}/stream_name?current_person_id=${personId}`,
          {
            method: 'GET',
            credentials: deviceToken ? 'omit' : 'include',
            headers: {
              'Accept': 'application/json',
              ...(deviceToken ? { Authorization: `Bearer ${deviceToken}` } : {}),
            },
            timeout: 5000
          }
        );

        if (response.status === 404) {
          // Document was deleted online — mark locally via the shared helper so a
          // row with unsynced edits gets deleted_online_had_unsynced_edits set
          // (the refresh GC keys its document.discarded audit off that flag). An
          // inline put here would drop unsynced edits with no discard tracking.
          const existingDoc = await db.documents.get(documentId);
          if (existingDoc) {
            await markDeletedOnline(existingDoc);
          }
          logger.warn(`Document ${documentId} not found on server (deleted online)`);
          return;
        }

        if (!response.ok) {
          throw new Error(`Failed to get stream name: ${response.status}`);
        }

        const body = await response.json();
        // The server returns a map of streams the user may view: `main` plus one
        // per permitted sensitive-identifier permission.
        const streamMap: Record<string, string> = body.signed_stream_names ?? {};

        logger.debug('Got signed stream names', { documentId, streams: Object.keys(streamMap), cancelled, mounted: mountedRef.current });

        if (cancelled || !mountedRef.current) {
          logger.warn('Cancelled or unmounted, skipping subscription', { documentId, cancelled, mounted: mountedRef.current });
          return;
        }

        // Subscribe to every stream the server granted. Each carries a partitioned
        // slice of the document (the main stream's non-sensitive fields, or one
        // permission key's fields); the onUpdate merge unions them into the doc.
        for (const [streamKey, signedStreamName] of Object.entries(streamMap)) {
          const subscription = websocketService.subscribeToDocument(
            signedStreamName,
            documentId,
            (data: unknown) => {
              try {
                logger.debug('Update received', { documentId, streamKey });

                const payload = data as Record<string, unknown>;
                const update: DocumentUpdate = {
                  content: (payload.content || data) as string,
                  timestamp: new Date().toISOString()
                };

                onUpdateRef.current?.(update, streamKey);
              } catch (callbackError) {
                // Don't let callback errors break the WebSocket
                logger.error('Error in update callback', callbackError);
              }
            },
            streamKey
          );
          subscriptionsRef.current.push(subscription);
        }

        logger.debug('Subscriptions created', { documentId, count: subscriptionsRef.current.length });
        setConnected(true);
        setError(null); // Clear any previous errors

      } catch (err) {
        // WebSocket errors should never crash the app
        const error = err instanceof Error ? err : new Error('Unknown error');
        logger.error('Subscription failed (app will continue normally)', error);
        if (mountedRef.current) {
          setError(error);
          setConnected(false);
          // Call error callback but don't throw
          try {
            onErrorRef.current?.(error);
          } catch (callbackError) {
            logger.error('Error in error callback', callbackError);
          }
        }
      }
    };

    subscribe();

    return () => {
      cancelled = true;
      for (const subscription of subscriptionsRef.current) {
        try {
          subscription.unsubscribe();
        } catch (unsubscribeError) {
          // Don't let unsubscribe errors break cleanup
          logger.error('Error during unsubscribe', unsubscribeError);
        }
      }
      subscriptionsRef.current = [];
      if (mountedRef.current) {
        setConnected(false);
      }
    };
  }, [clientId, documentId, personId, enabled, isOnline]);

  return { connected, error };
}
